import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { postDataAPI } from '../utils/fetchData';

// Custom hook for analytics tracking
export const useAnalyticsTracking = () => {
    const location = useLocation();
    const [sessionId, setSessionId] = useState(null);
    const [currentPageViewId, setCurrentPageViewId] = useState(null);
    const [currentAppUsageId, setCurrentAppUsageId] = useState(null);
    const sessionStartTime = useRef(null);
    const pageStartTime = useRef(null);
    const lastActivityTime = useRef(null);
    const heartbeatInterval = useRef(null);
    const scrollDepthRef = useRef(0);
    const user = useSelector(state => state.auth?.user);

    // Get user ID and email from Redux store or localStorage
    const getUserInfo = useCallback(() => {
        const userFromStorage = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
        const token = localStorage.getItem('dizeeToken');
        
        // Check for delegate access
        const selectedArtistData = localStorage.getItem('selectedArtist');
        let delegateInfo = null;
        
        if (selectedArtistData) {
            try {
                const selectedArtist = JSON.parse(selectedArtistData);
                delegateInfo = {
                    isDelegateAccess: true,
                    teamMemberUserId: user?._id || userFromStorage?._id,
                    teamMemberEmail: user?.email || userFromStorage?.email,
                    artistUserId: selectedArtist._id,
                    artistEmail: selectedArtist.email,
                    artistName: selectedArtist.name || selectedArtist.username
                };
            } catch (error) {
                console.error('Error parsing selectedArtist for analytics:', error);
            }
        }
        
        return {
            // ALWAYS use the actual logged-in user for analytics tracking consistency
            // This ensures session IDs match between creation and tracking calls
            userId: user?._id || userFromStorage?._id,
            userEmail: user?.email || userFromStorage?.email,
            token: token,
            // Add delegate information for reporting (but don't change the tracking user)
            delegateInfo: delegateInfo
        };
    }, [user]);

    // Start a new session
    const startSession = useCallback(async () => {
        try {
            const { userId, userEmail, token, delegateInfo } = getUserInfo();
            if (!userId || !userEmail || !token) return null;

            const sessionData = {};
            
            // Add delegate access information to session data
            if (delegateInfo) {
                sessionData.delegateAccess = delegateInfo;
            }

            const response = await postDataAPI('analytics-tracking/session/start', sessionData, token);
            
            if (response.data.success) {
                const newSessionId = response.data.sessionId;
                setSessionId(newSessionId);
                sessionStartTime.current = new Date();
                lastActivityTime.current = new Date();
                
                // Store session ID in localStorage for persistence
                localStorage.setItem('analyticsSessionId', newSessionId);
                
                if (delegateInfo) {
                    console.log(`📊 Analytics session started: ${newSessionId} (Team member: ${delegateInfo.teamMemberEmail} acting for: ${delegateInfo.artistEmail})`);
                } else {
                    console.log('📊 Analytics session started:', newSessionId);
                }
                return newSessionId;
            }
        } catch (error) {
            console.error('Error starting analytics session:', error);
        }
        return null;
    }, [getUserInfo]);

    // End current session
    const endSession = useCallback(async () => {
        try {
            const { token } = getUserInfo();
            if (!sessionId || !token) return;

            await postDataAPI('analytics-tracking/session/end', {}, token, {
                headers: {
                    'X-Session-Id': sessionId
                }
            });
            
            setSessionId(null);
            localStorage.removeItem('analyticsSessionId');
            console.log('📊 Analytics session ended');
        } catch (error) {
            console.error('Error ending analytics session:', error);
        }
    }, [sessionId, getUserInfo]);

    // Send session heartbeat
    const sendHeartbeat = useCallback(async () => {
        try {
            const { token } = getUserInfo();
            if (!sessionId || !token) return;

            await postDataAPI('analytics-tracking/session/heartbeat', {}, token, {
                headers: {
                    'X-Session-Id': sessionId
                }
            });
            
            lastActivityTime.current = new Date();
        } catch (error) {
            console.error('Error sending heartbeat:', error);
        }
    }, [sessionId, getUserInfo]);

    // Map route to app name
    const getAppNameFromRoute = useCallback((pathname) => {
        const routeMap = {
            '/': 'landing',
            '/bookings': 'bookings',
            '/catalog': 'catalog',
            '/merch': 'merch',
            '/analytics': 'analytics',
            '/charting': 'charting',
            '/playlisting': 'playlisting',
            '/social-listening': 'social-listening',
            '/spotify-revenue': 'spotify-revenue',
            '/more': 'more',
            '/focus': 'focus',
            '/vault': 'vault',
            '/experts': 'experts',
            '/prompts': 'prompts',
            '/marketplace': 'marketplace',
            '/team-member-dashboard': 'team-member-dashboard',
            '/manager-dashboard': 'manager-dashboard',
            '/apps': 'apps-hub',
            '/apps/all': 'all-apps'
        };

        // Find exact match first
        if (routeMap[pathname]) {
            return routeMap[pathname];
        }

        // Find partial match for nested routes
        for (const [route, appName] of Object.entries(routeMap)) {
            if (pathname.startsWith(route) && route !== '/') {
                return appName;
            }
        }

        return 'unknown';
    }, []);

    // Track app usage
    const trackAppUsage = useCallback(async (appName, route, referrer = null) => {
        try {
            const { userId, userEmail, token, delegateInfo } = getUserInfo();
            if (!userId || !userEmail || !token || !sessionId) return null;

            const trackingData = {
                appName,
                route,
                referrer
            };

            // Add delegate information to tracking data
            if (delegateInfo) {
                trackingData.delegateAccess = delegateInfo;
            }

            const response = await postDataAPI('analytics-tracking/track/app-usage', trackingData, token, {
                headers: {
                    'X-Session-Id': sessionId
                }
            });

            if (response.data.success) {
                const appUsageId = response.data.appUsageId;
                setCurrentAppUsageId(appUsageId);
                
                if (delegateInfo) {
                    console.log(`📱 App usage tracked: ${appName} (${delegateInfo.teamMemberEmail} → ${delegateInfo.artistEmail})`);
                } else {
                    console.log('📱 App usage tracked:', appName);
                }
                return appUsageId;
            }
        } catch (error) {
            console.error('Error tracking app usage:', error);
        }
        return null;
    }, [sessionId, getUserInfo]);

    // Track page view
    const trackPageView = useCallback(async (page, route, component = null, referrer = null) => {
        try {
            const { userId, userEmail, token, delegateInfo } = getUserInfo();
            if (!userId || !userEmail || !token || !sessionId) return null;

            const trackingData = {
                appUsageId: currentAppUsageId,
                page,
                route,
                component,
                referrer
            };

            // Add delegate information to tracking data
            if (delegateInfo) {
                trackingData.delegateAccess = delegateInfo;
            }

            const response = await postDataAPI('analytics-tracking/track/page-view', trackingData, token, {
                headers: {
                    'X-Session-Id': sessionId
                }
            });

            if (response.data.success) {
                const pageViewId = response.data.pageViewId;
                setCurrentPageViewId(pageViewId);
                pageStartTime.current = new Date();
                scrollDepthRef.current = 0;
                
                if (delegateInfo) {
                    console.log(`👁️ Page view tracked: ${page} (${delegateInfo.teamMemberEmail} → ${delegateInfo.artistEmail})`);
                } else {
                    console.log('👁️ Page view tracked:', page);
                }
                return pageViewId;
            }
        } catch (error) {
            console.error('Error tracking page view:', error);
        }
        return null;
    }, [sessionId, currentAppUsageId, getUserInfo]);

    // Track user interaction
    const trackInteraction = useCallback(async (type, element, text = null, data = null) => {
        try {
            const { token } = getUserInfo();
            if (!currentPageViewId || !token) return;

            await postDataAPI('analytics-tracking/track/interaction', {
                pageViewId: currentPageViewId,
                type,
                element,
                text,
                data
            }, token);

            console.log('🖱️ Interaction tracked:', type, element);
        } catch (error) {
            console.error('Error tracking interaction:', error);
        }
    }, [currentPageViewId, getUserInfo]);

    // Update scroll depth
    const updateScrollDepth = useCallback(async (scrollPercentage) => {
        try {
            const { token } = getUserInfo();
            if (!currentPageViewId || !token) return;

            // Only update if scroll depth increased significantly
            if (scrollPercentage > scrollDepthRef.current + 10) {
                scrollDepthRef.current = scrollPercentage;
                
                await postDataAPI('analytics-tracking/track/scroll-depth', {
                    pageViewId: currentPageViewId,
                    scrollDepth: scrollPercentage
                }, token);

                console.log('📜 Scroll depth updated:', scrollPercentage + '%');
            }
        } catch (error) {
            console.error('Error updating scroll depth:', error);
        }
    }, [currentPageViewId, getUserInfo]);

    // Set up scroll tracking
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100) || 0;
            
            if (scrollPercentage > scrollDepthRef.current) {
                updateScrollDepth(scrollPercentage);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [updateScrollDepth]);

    // Track route changes
    useEffect(() => {
        if (!sessionId) return;

        const appName = getAppNameFromRoute(location.pathname);
        const pageName = location.pathname === '/' ? 'landing' : location.pathname.split('/')[1] || 'unknown';
        
        // Track app usage for new app
        trackAppUsage(appName, location.pathname, document.referrer);
        
        // Track page view
        trackPageView(pageName, location.pathname, null, document.referrer);
        
    }, [location.pathname, sessionId, getAppNameFromRoute, trackAppUsage, trackPageView]);

    // Initialize session on mount
    useEffect(() => {
        // Check if we have an existing session
        const existingSessionId = localStorage.getItem('analyticsSessionId');
        
        if (existingSessionId) {
            setSessionId(existingSessionId);
        } else {
            startSession();
        }

        // Set up heartbeat interval (every 30 seconds)
        heartbeatInterval.current = setInterval(() => {
            sendHeartbeat();
        }, 30000);

        // Clean up on unmount
        return () => {
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
        };
    }, [startSession, sendHeartbeat]);

    // Handle page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden - user switched tabs or minimized
                console.log('📊 Page hidden - pausing tracking');
            } else {
                // Page is visible again - resume tracking
                console.log('📊 Page visible - resuming tracking');
                sendHeartbeat();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [sendHeartbeat]);

    // Handle beforeunload to end session
    useEffect(() => {
        const handleBeforeUnload = () => {
            endSession();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [endSession]);

    return {
        sessionId,
        currentPageViewId,
        currentAppUsageId,
        trackInteraction,
        updateScrollDepth,
        startSession,
        endSession,
        isTracking: !!sessionId
    };
};

export default useAnalyticsTracking; 